import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";

const SITE_NAME = "ARTERIA";
const DEFAULT_OG_IMAGE_PATH = "/assets/arteria-logo.png";
// Real, live company profiles (see Footer.tsx SOCIAL_LINKS) — YouTube/LinkedIn
// are left out here too since those hrefs are still null (not live yet).
const SAME_AS = [
  "https://web.facebook.com/arteria.eg",
  "https://www.instagram.com/arteria.eg",
];

const pageMetadata = {
  en: {
    "/": {
      title: "ARTERIA | Modeling, Casting and Creative Content",
      description: "Discover authentic talent and creative production through ARTERIA's modeling, casting and content services.",
    },
    "/about": {
      title: "About ARTERIA | Creative Talent and Production",
      description: "Learn how ARTERIA connects talent, brands and creative production through authentic storytelling.",
    },
    "/services": {
      title: "Creative Services | ARTERIA",
      description: "Explore ARTERIA's modeling, casting, video production and creative content services.",
    },
    "/talents": {
      title: "Discover Talent | ARTERIA",
      description: "Browse ARTERIA's creative talent for modeling, casting, production and branded content.",
    },
    "/content": {
      title: "Creative Content | ARTERIA",
      description: "Explore creative content and visual storytelling produced by ARTERIA.",
    },
    "/work": {
      title: "Our Work | ARTERIA",
      description: "View selected modeling, casting and creative production work from ARTERIA.",
    },
    "/spotlight": {
      title: "Spotlight | ARTERIA",
      description: "Read stories, insights and creative highlights from ARTERIA.",
    },
    "/contact": {
      title: "Contact ARTERIA",
      description: "Contact ARTERIA about talent, casting, production and creative content projects.",
    },
  },
  ar: {
    "/": {
      title: "أرتيريا | الموديلينج والكاستينج وصناعة المحتوى",
      description: "اكتشف المواهب الأصيلة وخدمات الإنتاج الإبداعي مع أرتيريا.",
    },
    "/about": {
      title: "عن أرتيريا | المواهب والإنتاج الإبداعي",
      description: "تعرّف على أرتيريا وكيف نربط المواهب والعلامات التجارية بالإنتاج الإبداعي.",
    },
    "/services": {
      title: "الخدمات الإبداعية | أرتيريا",
      description: "اكتشف خدمات أرتيريا في الموديلينج والكاستينج وإنتاج الفيديو والمحتوى الإبداعي.",
    },
    "/talents": {
      title: "اكتشف المواهب | أرتيريا",
      description: "تصفح المواهب الإبداعية لدى أرتيريا للموديلينج والكاستينج والإنتاج.",
    },
    "/content": {
      title: "المحتوى الإبداعي | أرتيريا",
      description: "استكشف المحتوى الإبداعي والقصص المرئية من أرتيريا.",
    },
    "/work": {
      title: "أعمالنا | أرتيريا",
      description: "شاهد نماذج من أعمال أرتيريا في الموديلينج والكاستينج والإنتاج الإبداعي.",
    },
    "/spotlight": {
      title: "سبوتلايت | أرتيريا",
      description: "اقرأ القصص والرؤى وأبرز الأعمال الإبداعية من أرتيريا.",
    },
    "/contact": {
      title: "تواصل مع أرتيريا",
      description: "تواصل مع أرتيريا حول المواهب والكاستينج والإنتاج والمحتوى الإبداعي.",
    },
  },
} as const;

type StaticPath = keyof typeof pageMetadata["en"];

// Nav translation key for each static page — reused for breadcrumb labels so
// they never drift from the labels already shown in the header.
const NAV_KEY_BY_PATH: Partial<Record<StaticPath, string>> = {
  "/about": "nav.about",
  "/services": "nav.services",
  "/talents": "nav.talents",
  "/content": "nav.content",
  "/work": "nav.work",
  "/spotlight": "nav.spotlight",
  "/contact": "nav.contact",
};

// Routes with no unique content of their own (auth walls, dashboards, forms,
// internal dev/test pages) — never worth indexing, per-route SEO can't help.
const privatePrefixes = [
  "/dashboard",
  "/admin",
  "/profile",
  "/notifications",
  "/casting-brief",
  "/client/profile",
  "/login",
  "/register",
  "/content-form",
  "/spotlight/submit",
  "/creator/me",
  "/ContentShowcase",
  "/CastingCTA",
];

const isPrivatePath = (pathname: string) =>
  privatePrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));

// While a dynamic page's own data is still loading (or a path never matches
// nothing at all), fall back to its nearest static-section metadata instead
// of the generic site-wide default -- e.g. "/spotlight/42" briefly shows the
// "/spotlight" title until the actual post's <PageSEO> mounts.
const DYNAMIC_PREFIX_FALLBACK: [prefix: string, staticPath: StaticPath][] = [
  ["/spotlight/", "/spotlight"],
  ["/talent/", "/talents"],
];

function resolveStaticMetadata(pathname: string, language: "en" | "ar") {
  const table = pageMetadata[language];
  if (pathname in table) return table[pathname as StaticPath];
  const match = DYNAMIC_PREFIX_FALLBACK.find(([prefix]) => pathname.startsWith(prefix));
  return match ? table[match[1]] : undefined;
}

export interface JsonLdEntity extends Record<string, unknown> {
  "@type": string;
}

function jsonLdScript(entity: JsonLdEntity, key: string | number) {
  return (
    <script key={key} type="application/ld+json">
      {JSON.stringify({ "@context": "https://schema.org", ...entity })}
    </script>
  );
}

/**
 * App-wide defaults, rendered once near the root (see App.tsx). Resolves
 * title/description/canonical/robots from the current route and renders the
 * sitewide Organization + WebSite JSON-LD plus a BreadcrumbList for static
 * top-level pages. Dynamic pages (spotlight/:id, talent/:id, creator/:id)
 * override the per-page tags with <PageSEO> rendered from the page itself —
 * see that component below.
 */
export default function SEO() {
  const { pathname } = useLocation();
  const { language, t } = useLanguage();
  const isPrivate = isPrivatePath(pathname);
  const metadata = resolveStaticMetadata(pathname, language);
  const title = metadata?.title ?? `${SITE_NAME} | Creative Talent and Production`;
  const description = metadata?.description ?? "ARTERIA connects creative talent, casting and production for authentic visual storytelling.";
  const origin = window.location.origin;
  const canonical = `${origin}${pathname}`;
  const defaultImage = `${origin}${DEFAULT_OG_IMAGE_PATH}`;

  const breadcrumb = pathname in pageMetadata[language] && pathname !== "/"
    ? [
      { name: t("nav.home"), url: `${origin}/` },
      { name: t(NAV_KEY_BY_PATH[pathname as StaticPath] ?? "nav.home"), url: canonical },
    ]
    : null;

  return (
    <Helmet>
      <html lang={language} dir={language === "ar" ? "rtl" : "ltr"} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={isPrivate ? "noindex,nofollow" : "index,follow"} />
      {!isPrivate && <link rel="canonical" href={canonical} />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={defaultImage} />
      <meta property="og:locale" content={language === "ar" ? "ar_AR" : "en_US"} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={defaultImage} />
      {!isPrivate && jsonLdScript({
        "@type": "Organization",
        name: SITE_NAME,
        url: origin,
        logo: defaultImage,
        sameAs: SAME_AS,
      }, "org")}
      {!isPrivate && jsonLdScript({
        "@type": "WebSite",
        name: SITE_NAME,
        url: origin,
      }, "website")}
      {!isPrivate && breadcrumb && jsonLdScript({
        "@type": "BreadcrumbList",
        itemListElement: breadcrumb.map((crumb, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: crumb.name,
          item: crumb.url,
        })),
      }, "breadcrumb")}
    </Helmet>
  );
}

export interface BreadcrumbEntry {
  name: string;
  path: string;
}

export interface PageSEOProps {
  /** Real title/description derived from the loaded entity — omit while data is still loading. */
  title?: string;
  description?: string;
  /** Absolute image URL. Falls back to the sitewide default when omitted. */
  image?: string;
  type?: "article" | "profile";
  /** Explicitly override indexability for this exact page (e.g. noindex a "not found" state). */
  noindex?: boolean;
  /** Extra structured data for this page (BlogPosting, Person, ...) — additive, never replaces the sitewide Organization/WebSite. */
  jsonLd?: JsonLdEntity | JsonLdEntity[];
  /** Breadcrumb trail after Home, e.g. [{name: "Spotlight", path: "/spotlight"}, {name: post.title, path: `/spotlight/${id}`}]. */
  breadcrumb?: BreadcrumbEntry[];
}

/**
 * Per-page override for routes whose real SEO metadata depends on fetched
 * data (spotlight/:id, talent/:id, creator/:id, 404s). Render this directly
 * in the page component once the data (or the "not found" state) is known —
 * react-helmet-async merges it with the sitewide <SEO/> defaults, so only
 * the tags you actually pass here change; everything else keeps the
 * route-level default.
 */
export function PageSEO({ title, description, image, type, noindex, jsonLd, breadcrumb }: PageSEOProps) {
  const { t } = useLanguage();
  const origin = window.location.origin;
  const entities = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  const fullBreadcrumb = breadcrumb
    ? [{ name: t("nav.home"), path: "/" }, ...breadcrumb]
    : null;

  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {noindex !== undefined && <meta name="robots" content={noindex ? "noindex,nofollow" : "index,follow"} />}
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {type && <meta property="og:type" content={type} />}
      {image && <meta property="og:image" content={image} />}
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      {image && <meta name="twitter:image" content={image} />}
      {!noindex && fullBreadcrumb && jsonLdScript({
        "@type": "BreadcrumbList",
        itemListElement: fullBreadcrumb.map((crumb, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: crumb.name,
          item: `${origin}${crumb.path}`,
        })),
      }, "breadcrumb")}
      {!noindex && entities.map((entity, i) => jsonLdScript(entity, `entity-${i}`))}
    </Helmet>
  );
}
